import { FC, useState, useEffect, useCallback, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Logo from "../Logo";
import Button from "../Button";
import NavDropdown, { DropdownItem } from "../NavDropdown";
import { globalContent } from "../../config/content";
import styles from "./Header.module.scss";

const PRODUCT_ITEMS: DropdownItem[] = [
  {
    label: "Digital Building Information",
    to: "/digital-building-information",
  },
  { label: "Digital O&M", to: "/digital-om" },
  { label: "Documents & Drawings", to: "/documents-drawings" },
  { label: "Asset Information", to: "/asset-information" },
  { label: "Search", to: "/search" },
  { label: "AI", to: "/ai" },
];

const SOLUTIONS_ITEMS: DropdownItem[] = [
  { label: "Who It’s For", to: "/who-its-for" },
];

// @todo: add resources back later
// const RESOURCES_ITEMS: DropdownItem[] = [
//   { label: "Resources", to: "/resources" },
// ];

const COMPANY_ITEMS: DropdownItem[] = [
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const Header: FC = () => {
  const location = useLocation();
  const isLegalPage = [
    "/privacy-policy",
    "/cookie-policy",
    "/terms-and-conditions",
  ].includes(location.pathname);

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(isLegalPage);

  useEffect(() => {
    const handleScroll = () => {
      if (isLegalPage) {
        setIsScrolled(true);
      } else {
        setIsScrolled(window.scrollY > 0);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isLegalPage]);

  const [expandedMobileSections, setExpandedMobileSections] = useState<
    Record<string, boolean>
  >({
    product: false,
    solutions: false,
    resources: false,
    company: false,
  });

  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleOpenDropdown = useCallback((name: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenDropdown(name);
  }, []);

  const closeDropdownImmediate = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenDropdown(null);
  }, []);

  const handleCloseDropdown = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
      closeTimeoutRef.current = null;
    }, 150);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Close menus when route changes
  useEffect(() => {
    closeDropdownImmediate();
    closeMobileMenu();
  }, [location.pathname, closeDropdownImmediate, closeMobileMenu]);

  // Handle escape key and body scroll lock
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (openDropdown) {
          closeDropdownImmediate();
        } else if (isMobileMenuOpen) {
          closeMobileMenu();
        }
      }
    };

    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
      if (openDropdown) {
        window.addEventListener("keydown", handleKeyDown);
      }
    }

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileMenuOpen, openDropdown, closeDropdownImmediate, closeMobileMenu]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const toggleMobileSection = (sectionKey: string) => {
    setExpandedMobileSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  // Helper to check if current route matches any item in a dropdown
  const isSectionActive = (items: DropdownItem[]) => {
    return items.some((item) => location.pathname === item.to);
  };

  const isProductActive = isSectionActive(PRODUCT_ITEMS);
  const isSolutionsActive = isSectionActive(SOLUTIONS_ITEMS);
  // @todo: add resources back later
  // const isResourcesActive = isSectionActive(RESOURCES_ITEMS);
  const isCompanyActive = isSectionActive(COMPANY_ITEMS);

  return (
    <header
      className={`${styles.header} ${isScrolled ? styles.scrolled : ""} ${
        !isScrolled && isMobileMenuOpen ? styles.mobileMenuOpenAtTop : ""
      }`}
      role="banner"
    >
      <div className={styles.inner}>
        {/* Pulse Logo (Links to /) */}
        <Logo variant={isScrolled ? "dark" : "light"} />

        {/* Desktop Navigation */}
        <nav className={styles.desktopNav} aria-label="Primary site navigation">
          <ul className={styles.navList}>
            {/* 1. Product Dropdown */}
            <li className={styles.navItem}>
              <NavDropdown
                label="Product"
                items={PRODUCT_ITEMS}
                isOpen={openDropdown === "product"}
                onToggle={() => {
                  if (openDropdown === "product") {
                    closeDropdownImmediate();
                  } else {
                    handleOpenDropdown("product");
                  }
                }}
                onOpen={() => handleOpenDropdown("product")}
                onClose={handleCloseDropdown}
                isActive={isProductActive}
                variant={isScrolled ? "dark" : "light"}
              />
            </li>

            {/* 2. Solutions Dropdown */}
            <li className={styles.navItem}>
              <NavDropdown
                label="Solutions"
                items={SOLUTIONS_ITEMS}
                isOpen={openDropdown === "solutions"}
                onToggle={() => {
                  if (openDropdown === "solutions") {
                    closeDropdownImmediate();
                  } else {
                    handleOpenDropdown("solutions");
                  }
                }}
                onOpen={() => handleOpenDropdown("solutions")}
                onClose={handleCloseDropdown}
                isActive={isSolutionsActive}
                variant={isScrolled ? "dark" : "light"}
              />
            </li>

            {/* 3. Resources Dropdown */}
            {/* @todo: add resources back later */}
            {/* <li className={styles.navItem}>
              <NavDropdown
                label="Resources"
                items={RESOURCES_ITEMS}
                isOpen={openDropdown === "resources"}
                onToggle={() => {
                  if (openDropdown === "resources") {
                    closeDropdownImmediate();
                  } else {
                    handleOpenDropdown("resources");
                  }
                }}
                onOpen={() => handleOpenDropdown("resources")}
                onClose={handleCloseDropdown}
                isActive={isResourcesActive}
                variant={isScrolled ? "dark" : "light"}
              />
            </li> */}

            {/* 4. Company Dropdown */}
            <li className={styles.navItem}>
              <NavDropdown
                label="Company"
                items={COMPANY_ITEMS}
                isOpen={openDropdown === "company"}
                onToggle={() => {
                  if (openDropdown === "company") {
                    closeDropdownImmediate();
                  } else {
                    handleOpenDropdown("company");
                  }
                }}
                onOpen={() => handleOpenDropdown("company")}
                onClose={handleCloseDropdown}
                isActive={isCompanyActive}
                variant={isScrolled ? "dark" : "light"}
              />
            </li>

            {/* 5. Explore the Demo Secondary Link */}
            <li className={styles.navItem}>
              <NavLink
                to={globalContent.demoUrl}
                className={({ isActive }) =>
                  isActive
                    ? `${styles.exploreLink} ${styles.active}`
                    : styles.exploreLink
                }
              >
                <span>Explore the Demo</span>
                <svg
                  className={styles.exploreArrow}
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M2.5 6H9.5M9.5 6L6.5 3M9.5 6L6.5 9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </NavLink>
            </li>
          </ul>
        </nav>

        {/* Desktop Primary CTA: Request a Demo */}
        <div className={styles.headerActions}>
          <Button to="/request-a-demo" variant="secondary" size="md">
            Request a Demo
          </Button>
        </div>

        {/* Mobile Navigation Toggle Button */}
        <button
          type="button"
          className={styles.mobileToggle}
          onClick={toggleMobileMenu}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-navigation-menu"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open navigation menu"}
        >
          <div
            className={`${styles.hamburgerIcon} ${
              isMobileMenuOpen ? styles.open : ""
            }`}
            aria-hidden="true"
          >
            <span />
            <span />
            <span />
          </div>
        </button>
      </div>

      {/* Mobile Navigation Drawer & Backdrop */}
      {isMobileMenuOpen && (
        <>
          <div
            className={styles.mobileMenuBackdrop}
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          <div
            id="mobile-navigation-menu"
            className={`${styles.mobileMenu} ${!isScrolled ? styles.atTop : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <nav aria-label="Mobile site navigation">
              <ul className={styles.mobileNavList}>
                {/* Product Accordion */}
                <li className={styles.mobileSection}>
                  <button
                    type="button"
                    className={`${styles.mobileAccordionButton} ${
                      expandedMobileSections.product ? styles.expanded : ""
                    }`}
                    onClick={() => toggleMobileSection("product")}
                    aria-expanded={expandedMobileSections.product}
                  >
                    <span>Product</span>
                    <svg
                      className={styles.accordionChevron}
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M2.5 4.5L6 8L9.5 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {expandedMobileSections.product && (
                    <ul className={styles.mobileSubmenu}>
                      {PRODUCT_ITEMS.map((item) => (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            onClick={closeMobileMenu}
                            className={({ isActive }) =>
                              `${styles.mobileSubLink} ${
                                isActive ? styles.activeItem : ""
                              }`
                            }
                          >
                            {item.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>

                {/* Solutions Accordion */}
                <li className={styles.mobileSection}>
                  <button
                    type="button"
                    className={`${styles.mobileAccordionButton} ${
                      expandedMobileSections.solutions ? styles.expanded : ""
                    }`}
                    onClick={() => toggleMobileSection("solutions")}
                    aria-expanded={expandedMobileSections.solutions}
                  >
                    <span>Solutions</span>
                    <svg
                      className={styles.accordionChevron}
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M2.5 4.5L6 8L9.5 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {expandedMobileSections.solutions && (
                    <ul className={styles.mobileSubmenu}>
                      {SOLUTIONS_ITEMS.map((item) => (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            onClick={closeMobileMenu}
                            className={({ isActive }) =>
                              `${styles.mobileSubLink} ${
                                isActive ? styles.activeItem : ""
                              }`
                            }
                          >
                            {item.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>

                {/* Resources Accordion */}
                {/* @todo: add resources back later */}
                {/* <li className={styles.mobileSection}>
                  <button
                    type="button"
                    className={`${styles.mobileAccordionButton} ${
                      expandedMobileSections.resources ? styles.expanded : ""
                    }`}
                    onClick={() => toggleMobileSection("resources")}
                    aria-expanded={expandedMobileSections.resources}
                  >
                    <span>Resources</span>
                    <svg
                      className={styles.accordionChevron}
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M2.5 4.5L6 8L9.5 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {expandedMobileSections.resources && (
                    <ul className={styles.mobileSubmenu}>
                      {RESOURCES_ITEMS.map((item) => (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            onClick={closeMobileMenu}
                            className={({ isActive }) =>
                              `${styles.mobileSubLink} ${
                                isActive ? styles.activeItem : ""
                              }`
                            }
                          >
                            {item.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li> */}

                {/* Company Accordion */}
                <li className={styles.mobileSection}>
                  <button
                    type="button"
                    className={`${styles.mobileAccordionButton} ${
                      expandedMobileSections.company ? styles.expanded : ""
                    }`}
                    onClick={() => toggleMobileSection("company")}
                    aria-expanded={expandedMobileSections.company}
                  >
                    <span>Company</span>
                    <svg
                      className={styles.accordionChevron}
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M2.5 4.5L6 8L9.5 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {expandedMobileSections.company && (
                    <ul className={styles.mobileSubmenu}>
                      {COMPANY_ITEMS.map((item) => (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            onClick={closeMobileMenu}
                            className={({ isActive }) =>
                              `${styles.mobileSubLink} ${
                                isActive ? styles.activeItem : ""
                              }`
                            }
                          >
                            {item.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>

                {/* Explore the Demo Link */}
                <li>
                  <NavLink
                    to={globalContent.demoUrl}
                    onClick={closeMobileMenu}
                    className={({ isActive }) =>
                      `${styles.mobileExploreLink} ${
                        isActive ? styles.activeItem : ""
                      }`
                    }
                  >
                    <span>Explore the Demo</span>
                    <svg
                      className={styles.exploreArrow}
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M2.5 6H9.5M9.5 6L6.5 3M9.5 6L6.5 9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </NavLink>
                </li>
              </ul>
            </nav>

            {/* Mobile Primary CTA */}
            <div className={styles.mobileCtaWrapper}>
              <Button
                to="/request-a-demo"
                variant="secondary"
                size="lg"
                fullWidth
                onClick={closeMobileMenu}
              >
                Request a Demo
              </Button>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
